// Store only a scope fingerprint, random request ID and its age. Never persist tokens or render inputs.
const databaseName = "flair-render-admissions-v2";
const storeName = "requests";
const maximumPending = 64;
// No function runs past an hour, so an older key cannot be protecting an in-flight request.
const maximumAgeMs = 60 * 60 * 1000;
const requestIdPattern = /^[a-zA-Z0-9_-]{1,128}$/;
let database: Promise<IDBDatabase> | undefined;
export class RenderAdmissionError extends Error {}

export function readStoredRequest(value: unknown) {
  // Earlier builds stored only the ID, so those keys have no age and count as expired.
  const { id, createdAt } = (
    typeof value === "string" ? { id: value, createdAt: 0 } : (value ?? {})
  ) as {
    id?: unknown;
    createdAt?: unknown;
  };
  return typeof id === "string" && requestIdPattern.test(id) && typeof createdAt === "number"
    ? { id, createdAt }
    : undefined;
}

// Keep the store bounded without ever refusing new work: drop expired keys, then the oldest.
export function selectRequestsToEvict<Key>(
  requests: Array<{ key: Key; createdAt: number }>,
  now: number,
) {
  const isExpired = ({ createdAt }: { createdAt: number }) => now - createdAt >= maximumAgeMs;
  const live = requests
    .filter((request) => !isExpired(request))
    .sort((first, second) => first.createdAt - second.createdAt);
  return [
    ...requests.filter(isExpired),
    ...live.slice(0, Math.max(0, live.length - maximumPending + 1)),
  ].map(({ key }) => key);
}

function openDatabase(): Promise<IDBDatabase> {
  if (!database) {
    database = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(databaseName, 1);
      let abandoned = false;
      request.onupgradeneeded = () => request.result.createObjectStore(storeName);
      request.onerror = () => {
        abandoned = true;
        reject(new RenderAdmissionError("Render retry storage is unavailable."));
      };
      request.onblocked = () => {
        abandoned = true;
        reject(new RenderAdmissionError("Close older Flair tabs before rendering."));
      };
      request.onsuccess = () => {
        const db = request.result;
        if (abandoned) {
          db.close();
          return;
        }
        db.onversionchange = () => {
          db.close();
          database = undefined;
        };
        resolve(db);
      };
    }).catch((error) => {
      database = undefined;
      throw error;
    });
  }
  return database;
}

export async function renderAdmissionFingerprint(scope: {
  endpoint: string;
  userId: string;
  publicTeamId: string;
  numOutputs: number;
  body: string;
}) {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(
      JSON.stringify([
        scope.endpoint,
        scope.userId,
        scope.publicTeamId,
        scope.numOutputs,
        scope.body,
      ]),
    ),
  );
  return Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, "0")).join(
    "",
  );
}

export async function getRenderAdmissionRequest(
  fingerprint: string,
  preferredId?: string,
): Promise<string> {
  if (
    preferredId !== undefined &&
    (typeof preferredId !== "string" || !requestIdPattern.test(preferredId))
  ) {
    throw new RenderAdmissionError("Invalid prediction request key.");
  }
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    // A read/write transaction serializes creation across tabs and survives an ambiguous fetch/reload.
    const transaction = db.transaction(storeName, "readwrite");
    const store = transaction.objectStore(storeName);
    let requestId: string;
    const failure = "Render retry storage is unavailable.";
    transaction.oncomplete = () => resolve(requestId);
    transaction.onabort = () => reject(new RenderAdmissionError(failure));
    transaction.onerror = () => reject(new RenderAdmissionError(failure));
    const existing = store.get(fingerprint);
    existing.onsuccess = () => {
      const now = Date.now();
      const stored = readStoredRequest(existing.result);
      if (stored && now - stored.createdAt < maximumAgeMs) {
        requestId = stored.id;
        return;
      }
      const keys = store.getAllKeys();
      const values = store.getAll();
      values.onsuccess = () => {
        const requests = keys.result.map((key, index) => ({
          key,
          createdAt: readStoredRequest(values.result[index])?.createdAt ?? 0,
        }));
        selectRequestsToEvict(requests, now).forEach((key) => store.delete(key));
        requestId = preferredId ?? crypto.randomUUID();
        store.put({ id: requestId, createdAt: now }, fingerprint);
      };
    };
  });
}

export async function confirmRenderAdmissionRequest(fingerprint: string, requestId: string) {
  const db = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(storeName, "readwrite");
    const store = transaction.objectStore(storeName);
    transaction.oncomplete = () => resolve();
    transaction.onabort = () =>
      reject(new RenderAdmissionError("Render receipt storage is unavailable."));
    transaction.onerror = () =>
      reject(new RenderAdmissionError("Render receipt storage is unavailable."));
    const existing = store.get(fingerprint);
    existing.onsuccess = () => {
      if (readStoredRequest(existing.result)?.id === requestId) {
        store.delete(fingerprint);
      }
    };
  });
}
